/* eslint-disable @typescript-eslint/consistent-type-assertions */
/* eslint-disable @atlassian/relay/unused-fields */

import React, { useCallback, useMemo, useState } from 'react';
import { styled } from '@compiled/react';
import { graphql, useFragment } from 'react-relay';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import ChevronDownIcon from '@atlaskit/icon/utility/migration/chevron-down';
import ChevronRightIcon from '@atlaskit/icon/utility/migration/chevron-right';
import { Box, Inline, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import { fireTrackAnalytics, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import type { JiraNotificationPreferenceInput } from '@atlassian/jira-relay/src/__generated__/globalNotificationsConfigHead_HeadCheckbox_updateGlobalPreferencesMutation.graphql';
import type {
	notificationsAccordianRow_personalSettingsNotificationsConfigTable_NotificationsConfigRow_allPreferences$key,
	JiraNotificationType,
} from '@atlassian/jira-relay/src/__generated__/notificationsAccordianRow_personalSettingsNotificationsConfigTable_NotificationsConfigRow_allPreferences.graphql';
import type { notificationsAccordianRow_personalSettingsNotificationsConfigTable_NotificationsConfigRow_enabledDataFragment$key } from '@atlassian/jira-relay/src/__generated__/notificationsAccordianRow_personalSettingsNotificationsConfigTable_NotificationsConfigRow_enabledDataFragment.graphql';
import type { JiraNotificationChannelType } from '@atlassian/jira-relay/src/__generated__/notificationsConfigRow_personalSettingsNotificationsConfigTable_allPreferences.graphql';
import { functionWithCondition } from '@atlassian/jira-feature-flagging-utils';
import { fg } from '@atlassian/jira-feature-gating';
import { EMAIL, IN_PRODUCT } from '../../constants.tsx';
import type {
	HeadCheckboxIdsType,
	JiraNotificationCategoryType,
	JiraNotificationGraphQLType,
	JiraNotificationPreference,
	JiraNotificationPreferenceWithCategory,
} from '../../types.tsx';
import messages from '../messages.tsx';
import { NotificationsConfigCheckbox } from '../notifications-config-checkbox/index.tsx';
import { NotificationsConfigRow as NotificationsConfigRowLite } from '../notifications-config-row-lite/index.tsx';
import { HeadCellContainer } from '../styled.tsx';

const ATTRIBUTES_FOR_SINGLE_ROW_UPDATE = {
	type: 'row',
	issueAssigned: 'unchanged',
	issueUpdated: 'unchanged',
	issueDeleted: 'unchanged',
	commentCreated: 'unchanged',
	commentEdited: 'unchanged',
	commentDeleted: 'unchanged',
	issueCreated: 'unchanged',
	issueMoved: 'unchanged',
	mentionsCombined: 'unchanged',
	worklogCombined: 'unchanged',
	miscellaneousIssueEventCombined: 'unchanged',
};

const NOTIFICATIONS_CONFIG_TYPE_MAP_WITH_ISSUE_TERM: Record<string, string> = {
	ISSUE_ASSIGNED: 'issueAssignedType',
	ISSUE_UPDATED: 'issueUpdatedType',
	ISSUE_DELETED: 'issueDeletedType',
	COMMENT_CREATED: 'commentCreatedType',
	COMMENT_EDITED: 'commentEditedType',
	COMMENT_DELETED: 'commentDeletedType',
	ISSUE_CREATED: 'issueCreatedType',
	ISSUE_MOVED: 'issueMovedType',
	MENTIONS_COMBINED: 'mentionsCombinedType',
	WORKLOG_COMBINED: 'worklogCombinedType',
	MISCELLANEOUS_ISSUE_EVENT_COMBINED: 'miscellaneousIssueEventCombined',
};

const NOTIFICATIONS_CONFIG_TYPE_MAP_ISSUE_TERM_REFRESH: Record<string, string> = {
	ISSUE_ASSIGNED: 'issueAssignedTypeIssueTermRefresh',
	ISSUE_UPDATED: 'issueUpdatedTypeIssueTermRefresh',
	ISSUE_DELETED: 'issueDeletedTypeIssueTermRefresh',
	COMMENT_CREATED: 'commentCreatedType',
	COMMENT_EDITED: 'commentEditedType',
	COMMENT_DELETED: 'commentDeletedType',
	ISSUE_CREATED: 'issueCreatedTypeIssueTermRefresh',
	ISSUE_MOVED: 'issueMovedTypeIssueTermRefresh',
	MENTIONS_COMBINED: 'mentionsCombinedTypeIssueTermRefresh',
	WORKLOG_COMBINED: 'worklogCombinedType',
	MISCELLANEOUS_ISSUE_EVENT_COMBINED: 'miscellaneousIssueEventCombinedIssueTermRefresh',
};

const getNotificationsConfTypeMap = functionWithCondition(
	() => fg('jira-issue-terminology-refresh-m3'),
	() => NOTIFICATIONS_CONFIG_TYPE_MAP_ISSUE_TERM_REFRESH,
	() => NOTIFICATIONS_CONFIG_TYPE_MAP_WITH_ISSUE_TERM,
);

const NOTIFICATIONS_CONFIG_TYPE_MAP = getNotificationsConfTypeMap();

const CHECKBOX_STATES = {
	CHECKED: 'checked',
	INDERMINATE: 'indeterminate',
	UNCHECKED: 'unchecked',
};

const snakeToCamelCase = (str: string) =>
	str.toLowerCase().replace(/([-_]\w)/g, (g) => g[1].toUpperCase());

type HeadCheckboxMutationType = (
	newPreferencesAsList: readonly JiraNotificationPreferenceInput[],
	newPreferences: {
		[key: string]: JiraNotificationPreference;
	},
) => void;

type NotificationRowProps = {
	label: string;
	id: string;
	headCheckboxIds: HeadCheckboxIdsType;
	allPreferences: notificationsAccordianRow_personalSettingsNotificationsConfigTable_NotificationsConfigRow_allPreferences$key;
	enabledDataFragment: notificationsAccordianRow_personalSettingsNotificationsConfigTable_NotificationsConfigRow_enabledDataFragment$key;
	onRowMutation: (
		channel: JiraNotificationChannelType,
		isEnabled: boolean,
		newPreferences: {
			[key: string]: JiraNotificationPreference;
		},
		type: JiraNotificationType,
	) => void;
	onEmailHeadMutation: HeadCheckboxMutationType;
	onInProductHeadMutation: HeadCheckboxMutationType;
	category: JiraNotificationCategoryType;
	projectId?: string;
};

export const NotificationsConfigRow = ({
	label,
	id,
	headCheckboxIds,
	allPreferences,
	onRowMutation,
	onEmailHeadMutation,
	onInProductHeadMutation,
	enabledDataFragment,
	category,
	projectId = '',
}: NotificationRowProps) => {
	const { formatMessage } = useIntl();
	const [isOpen, setIsOpen] = useState(false);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const onToggle = useCallback(
		(event: React.SyntheticEvent<HTMLDetailsElement, Event>) => {
			if (event.currentTarget.open && !isOpen) {
				setIsOpen(true);
			} else if (!event.currentTarget.open && isOpen) {
				setIsOpen(false);
			}
		},
		[isOpen],
	);
	const emailCheckboxId = `email-${id}`;
	const inProductCheckboxId = `inProduct-${id}`;

	const Chevron = isOpen ? ChevronDownIcon : ChevronRightIcon;
	const enabledData = useFragment(
		graphql`
			fragment notificationsAccordianRow_personalSettingsNotificationsConfigTable_NotificationsConfigRow_enabledDataFragment on JiraNotificationGlobalPreference {
				options @required(action: THROW) {
					isEmailNotificationEnabled
				}
			}
		`,
		enabledDataFragment,
	);

	const allData = useFragment(
		graphql`
			fragment notificationsAccordianRow_personalSettingsNotificationsConfigTable_NotificationsConfigRow_allPreferences on JiraNotificationPreferences {
				issueAssigned @required(action: THROW) {
					id @required(action: THROW)
					type @required(action: THROW)
					emailChannel @required(action: THROW) {
						channel
						isEnabled
						isEditable
					}
					inProductChannel {
						channel
						isEnabled
						isEditable
					}
					category @required(action: THROW)
				}
				issueCreated @required(action: THROW) {
					id @required(action: THROW)
					type @required(action: THROW)
					emailChannel @required(action: THROW) {
						channel
						isEnabled
						isEditable
					}
					inProductChannel {
						channel
						isEnabled
						isEditable
					}
					category @required(action: THROW)
				}
				issueUpdated @required(action: THROW) {
					id @required(action: THROW)
					type @required(action: THROW)
					emailChannel @required(action: THROW) {
						channel
						isEnabled
						isEditable
					}
					inProductChannel {
						channel
						isEnabled
						isEditable
					}
					category @required(action: THROW)
				}
				issueDeleted @required(action: THROW) {
					id @required(action: THROW)
					type @required(action: THROW)
					emailChannel @required(action: THROW) {
						channel
						isEnabled
						isEditable
					}
					inProductChannel {
						channel
						isEnabled
						isEditable
					}
					category @required(action: THROW)
				}
				commentCreated @required(action: THROW) {
					id @required(action: THROW)
					type @required(action: THROW)
					emailChannel @required(action: THROW) {
						channel
						isEnabled
						isEditable
					}
					inProductChannel {
						channel
						isEnabled
						isEditable
					}
					category @required(action: THROW)
				}
				commentEdited @required(action: THROW) {
					id @required(action: THROW)
					type @required(action: THROW)
					emailChannel @required(action: THROW) {
						channel
						isEnabled
						isEditable
					}
					inProductChannel {
						channel
						isEnabled
						isEditable
					}
					category @required(action: THROW)
				}
				commentDeleted @required(action: THROW) {
					id @required(action: THROW)
					type @required(action: THROW)
					emailChannel @required(action: THROW) {
						channel
						isEnabled
						isEditable
					}
					inProductChannel {
						channel
						isEnabled
						isEditable
					}
					category @required(action: THROW)
				}
				issueMoved @required(action: THROW) {
					id @required(action: THROW)
					type @required(action: THROW)
					emailChannel @required(action: THROW) {
						channel
						isEnabled
						isEditable
					}
					inProductChannel {
						channel
						isEnabled
						isEditable
					}
					category @required(action: THROW)
				}
				mentionsCombined @required(action: THROW) {
					id @required(action: THROW)
					type @required(action: THROW)
					emailChannel @required(action: THROW) {
						channel
						isEnabled
						isEditable
					}
					inProductChannel {
						channel
						isEnabled
						isEditable
					}
					category @required(action: THROW)
				}
				worklogCombined @required(action: THROW) {
					id @required(action: THROW)
					type @required(action: THROW)
					emailChannel @required(action: THROW) {
						channel
						isEnabled
						isEditable
					}
					inProductChannel {
						channel
						isEnabled
						isEditable
					}
					category @required(action: THROW)
				}
				miscellaneousIssueEventCombined @required(action: THROW) {
					id @required(action: THROW)
					type @required(action: THROW)
					emailChannel @required(action: THROW) {
						channel
						isEnabled
						isEditable
					}
					inProductChannel {
						channel
						isEnabled
						isEditable
					}
					category @required(action: THROW)
				}
			}
		`,
		allPreferences,
	);

	const createJiraNotificationPreference = useCallback(
		(
			notificationType: JiraNotificationGraphQLType,
			isEnabled: boolean,
			channel: JiraNotificationChannelType,
			changedNotificationType: JiraNotificationType,
		) => {
			const retVal: JiraNotificationPreference = {
				type: allData[notificationType]?.type,
				id: allData[notificationType]?.id ?? '',
				emailChannel: {
					channel: allData[notificationType]?.emailChannel.channel,
					isEnabled:
						changedNotificationType === allData[notificationType]?.type && channel === EMAIL
							? isEnabled
							: allData[notificationType]?.emailChannel.isEnabled,
					isEditable: allData[notificationType]?.emailChannel.isEditable,
				},
			};

			const inProductChannelName = allData[notificationType]?.inProductChannel;

			if (inProductChannelName) {
				retVal.inProductChannel = {
					channel: inProductChannelName.channel,
					isEnabled:
						changedNotificationType === allData[notificationType]?.type && channel === IN_PRODUCT
							? isEnabled
							: inProductChannelName.isEnabled,
					isEditable: inProductChannelName.isEditable,
				};
			}

			return retVal;
		},
		[allData],
	);

	const onCheckboxChange = useCallback(
		(
			event: React.ChangeEvent<HTMLInputElement>,
			channel: JiraNotificationChannelType,
			type: JiraNotificationType | null | undefined,
		) => {
			const isEnabled = event.target.checked;
			if (!type) {
				return;
				// TODO: Add error handling
			}
			const newPreferences: {
				[key: string]: JiraNotificationPreference;
			} = {
				issueAssigned: createJiraNotificationPreference('issueAssigned', isEnabled, channel, type),
				issueCreated: createJiraNotificationPreference('issueCreated', isEnabled, channel, type),
				issueUpdated: createJiraNotificationPreference('issueUpdated', isEnabled, channel, type),
				issueDeleted: createJiraNotificationPreference('issueDeleted', isEnabled, channel, type),
				commentCreated: createJiraNotificationPreference(
					'commentCreated',
					isEnabled,
					channel,
					type,
				),
				commentEdited: createJiraNotificationPreference('commentEdited', isEnabled, channel, type),
				commentDeleted: createJiraNotificationPreference(
					'commentDeleted',
					isEnabled,
					channel,
					type,
				),
				issueMoved: createJiraNotificationPreference('issueMoved', isEnabled, channel, type),
				mentionsCombined: createJiraNotificationPreference(
					'mentionsCombined',
					isEnabled,
					channel,
					type,
				),
				worklogCombined: createJiraNotificationPreference(
					'worklogCombined',
					isEnabled,
					channel,
					type,
				),
				miscellaneousIssueEventCombined: createJiraNotificationPreference(
					'miscellaneousIssueEventCombined',
					isEnabled,
					channel,
					type,
				),
			};

			onRowMutation(channel, isEnabled, newPreferences, type);

			const camelType = snakeToCamelCase(type);
			const trackAnalyticsAttributes = ATTRIBUTES_FOR_SINGLE_ROW_UPDATE;
			fireTrackAnalytics(createAnalyticsEvent({}), 'checkbox updated', 'notificationCheckbox', {
				channel,
				...trackAnalyticsAttributes,
				[camelType]: isEnabled ? 'checked' : 'unchecked',
			});
			fireUIAnalytics(createAnalyticsEvent({}), 'checkbox clicked', camelType, {
				channel,
				value: isEnabled ? 'checked' : 'unchecked',
			});
		},
		[onRowMutation, createAnalyticsEvent, createJiraNotificationPreference],
	);

	const getChildren = useCallback(() => {
		const childrenDetails: {
			prefrerences: JiraNotificationPreference[];
			noOfEmailOptions: number;
			noOfInProductOptions: number;
			noOfEmailOptionsChecked: number;
			noOfInProductOptionsChecked: number;
		} = {
			prefrerences: [],
			noOfEmailOptions: 0,
			noOfInProductOptions: 0,
			noOfEmailOptionsChecked: 0,
			noOfInProductOptionsChecked: 0,
		};

		const preferences = Object.values(allData) as JiraNotificationPreferenceWithCategory[];
		const relevantPreferences = preferences.filter((pref) => pref.category === category);

		childrenDetails.prefrerences = relevantPreferences;
		relevantPreferences.forEach((preference) => {
			childrenDetails.noOfEmailOptions += preference.emailChannel.isEditable ? 1 : 0;
			childrenDetails.noOfInProductOptions += preference.inProductChannel?.isEditable ? 1 : 0;
			childrenDetails.noOfEmailOptionsChecked += preference.emailChannel.isEnabled ? 1 : 0;
			childrenDetails.noOfInProductOptionsChecked += preference.inProductChannel?.isEnabled ? 1 : 0;
		});

		return childrenDetails;
	}, [allData, category]);

	const getCheckboxState = (totalOptions: number, checkedOptions: number) => {
		if (checkedOptions === 0) return CHECKBOX_STATES.UNCHECKED;
		if (totalOptions === checkedOptions) return CHECKBOX_STATES.CHECKED;
		return CHECKBOX_STATES.INDERMINATE;
	};
	const getHeaderCheckboxStatus = useCallback(
		(channel: JiraNotificationChannelType) => {
			const {
				noOfEmailOptions,
				noOfEmailOptionsChecked,
				noOfInProductOptions,
				noOfInProductOptionsChecked,
			} = getChildren();

			if (channel === EMAIL) {
				return getCheckboxState(noOfEmailOptions, noOfEmailOptionsChecked);
			}

			if (channel === IN_PRODUCT) {
				return getCheckboxState(noOfInProductOptions, noOfInProductOptionsChecked);
			}
		},
		[getChildren],
	);

	const onHeadCheckboxChange = useCallback(
		(_event: React.ChangeEvent<HTMLInputElement>, channel: JiraNotificationChannelType) => {
			// For a channel, if no notification types are selected or indeterminate, enable the channel.
			// Else disable the channel.
			const {
				noOfEmailOptions,
				noOfInProductOptions,
				noOfInProductOptionsChecked,
				noOfEmailOptionsChecked,
				prefrerences,
			} = getChildren();
			const newPreferencesAsList: JiraNotificationPreferenceInput[] = [];
			const newEmailChannelEnabledValue = noOfEmailOptionsChecked < noOfEmailOptions;
			const newInProductChannelEnabledValue = noOfInProductOptionsChecked < noOfInProductOptions;
			const getUpdatedEnabledValueForCurrentChannel = () => {
				if (channel === EMAIL) return newEmailChannelEnabledValue;
				if (channel === IN_PRODUCT) return newInProductChannelEnabledValue;
			};
			const newPreferencesWithChannels = prefrerences.reduce(
				(acc: { [key: string]: JiraNotificationPreference }, notificationPreference) => {
					if (channel === EMAIL) {
						if (notificationPreference?.emailChannel.isEditable) {
							if (notificationPreference.type) {
								acc[snakeToCamelCase(notificationPreference.type)] =
									createJiraNotificationPreference(
										snakeToCamelCase(notificationPreference.type) as JiraNotificationGraphQLType,
										newEmailChannelEnabledValue,
										'EMAIL',
										notificationPreference.type,
									);
								newPreferencesAsList.push({
									type: notificationPreference.type,
									isEnabled: newEmailChannelEnabledValue,
									channel,
								});
							} else {
								// TODO: Throw error
							}
						}
					} else if (channel === IN_PRODUCT) {
						if (notificationPreference?.inProductChannel?.isEditable) {
							if (notificationPreference.type) {
								acc[snakeToCamelCase(notificationPreference.type)] =
									createJiraNotificationPreference(
										snakeToCamelCase(notificationPreference.type) as JiraNotificationGraphQLType,
										newInProductChannelEnabledValue,
										'IN_PRODUCT',
										notificationPreference.type,
									);
								newPreferencesAsList.push({
									type: notificationPreference.type,
									isEnabled: newInProductChannelEnabledValue,
									channel,
								});
							} else {
								// TODO: Throw error
							}
						}
					}
					return acc;
				},
				{},
			);

			if (allData) {
				const checkBoxStatus = getHeaderCheckboxStatus(channel);
				let previousValue = 'unchecked';
				let currentValue = 'checked';
				if (checkBoxStatus === CHECKBOX_STATES.INDERMINATE) {
					previousValue = 'mixed';
					currentValue = 'checked';
				} else if (checkBoxStatus === CHECKBOX_STATES.CHECKED) {
					previousValue = 'checked';
					currentValue = 'unchecked';
				}

				fireTrackAnalytics(createAnalyticsEvent({}), 'checkbox updated', 'notificationCheckbox', {
					type: 'all',
					...Object.keys(newPreferencesWithChannels).reduce<Record<string, string>>((acc, key) => {
						let oldPreference;
						if (channel === EMAIL) {
							oldPreference = (allData[key as keyof typeof allData] as JiraNotificationPreference)
								.emailChannel.isEnabled;
						} else if (channel === IN_PRODUCT) {
							oldPreference = (allData[key as keyof typeof allData] as JiraNotificationPreference)
								.inProductChannel?.isEnabled;
						}
						const newPreference = getUpdatedEnabledValueForCurrentChannel();

						if (oldPreference === newPreference) {
							acc[key] = 'unchanged';
						} else if (newPreference) {
							acc[key] = 'checked';
						} else {
							acc[key] = 'unchecked';
						}
						return acc;
					}, {}),
					channel,
				});
				fireUIAnalytics(createAnalyticsEvent({}), 'checkbox clicked', 'all', {
					channel,
					previousValue,
					currentValue,
				});

				if (channel === EMAIL) {
					onEmailHeadMutation(newPreferencesAsList, newPreferencesWithChannels);
				}

				if (channel === IN_PRODUCT) {
					onInProductHeadMutation(newPreferencesAsList, newPreferencesWithChannels);
				}
			}
		},
		[
			getChildren,
			allData,
			createJiraNotificationPreference,
			getHeaderCheckboxStatus,
			createAnalyticsEvent,
			onEmailHeadMutation,
			onInProductHeadMutation,
		],
	);

	const renderChildren = useMemo(() => {
		const { prefrerences } = getChildren();

		return prefrerences.map((notificationPreference, index) => (
			<NotificationsConfigRowLite
				preference={notificationPreference}
				key={notificationPreference.id}
				headCheckboxIds={headCheckboxIds}
				label={formatMessage(
					messages[
						NOTIFICATIONS_CONFIG_TYPE_MAP[
							notificationPreference.type as keyof typeof NOTIFICATIONS_CONFIG_TYPE_MAP
						] as keyof typeof messages
					],
				)}
				isOpen={isOpen}
				onChange={onCheckboxChange}
				isLastChild={index === prefrerences.length - 1}
				id={`${snakeToCamelCase(
					notificationPreference.type as keyof typeof NOTIFICATIONS_CONFIG_TYPE_MAP,
				)}${projectId ? `-${projectId}` : ''}`}
				isEmailNotificationDisabled={enabledData.options.isEmailNotificationEnabled === false}
			/>
		));
	}, [
		formatMessage,
		getChildren,
		headCheckboxIds,
		isOpen,
		onCheckboxChange,
		enabledData,
		projectId,
	]);

	return (
		<>
			<TableRowContainer>
				<HeadCellContainer>
					<details
						data-testid={`personal-settings-notifications-config-table.common.ui.accordion.accordion-wrapper-${id}`}
						onToggle={onToggle}
						open={isOpen}
					>
						<AccordianHeader>
							<Inline alignBlock="center">
								<Box xcss={headerChevronStyles} paddingInline="space.100">
									<Chevron
										color={token('color.icon')}
										LEGACY_primaryColor={token('color.icon')}
										label=""
									/>
								</Box>
								{label}
							</Inline>
						</AccordianHeader>
					</details>
				</HeadCellContainer>
				<HeadCellContainer>
					<NotificationsConfigCheckbox
						aria-labelledby={`${id} ${headCheckboxIds.IN_PRODUCT}`}
						aria-label={formatMessage(messages.inProductColumnHeader)}
						id={inProductCheckboxId}
						name={inProductCheckboxId}
						testId={`personal-settings-notifications-config-table.ui.notifications-config-row-accordion.${inProductCheckboxId}`}
						isChecked={
							getHeaderCheckboxStatus(IN_PRODUCT) === CHECKBOX_STATES.CHECKED ||
							getHeaderCheckboxStatus(IN_PRODUCT) === CHECKBOX_STATES.INDERMINATE
						}
						isIndeterminate={getHeaderCheckboxStatus(IN_PRODUCT) === CHECKBOX_STATES.INDERMINATE}
						onChange={(ev) => onHeadCheckboxChange(ev, IN_PRODUCT)}
					/>
				</HeadCellContainer>
				<HeadCellContainer>
					<NotificationsConfigCheckbox
						aria-labelledby={`${id} ${headCheckboxIds.EMAIL}`}
						aria-label={formatMessage(messages.emailColumnHeader)}
						id={emailCheckboxId}
						isDisabled={enabledData.options.isEmailNotificationEnabled === false}
						name={emailCheckboxId}
						testId={`personal-settings-notifications-config-table.ui.notifications-config-row-accordion.${emailCheckboxId}`}
						isChecked={
							getHeaderCheckboxStatus(EMAIL) === CHECKBOX_STATES.CHECKED ||
							getHeaderCheckboxStatus(EMAIL) === CHECKBOX_STATES.INDERMINATE
						}
						isIndeterminate={getHeaderCheckboxStatus(EMAIL) === CHECKBOX_STATES.INDERMINATE}
						onChange={(ev) => onHeadCheckboxChange(ev, EMAIL)}
					/>
				</HeadCellContainer>
			</TableRowContainer>
			<>{renderChildren}</>
		</>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const AccordianHeader = styled.summary({
	listStyle: 'none',
	cursor: 'pointer',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TableRowContainer = styled.tr({
	height: `${token('space.600')}`,

	'&:hover': {
		backgroundColor: token('color.background.neutral.subtle.hovered'),
	},
});

const headerChevronStyles = xcss({
	verticalAlign: 'middle',
});
